<script setup lang="ts">
import { computed, ref, toRef } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useMutation } from '@tanstack/vue-query'
import { useAuth } from '@/stores/auth'
import { useGameState } from '@/stores/gameState'

import { useQuery } from '@tanstack/vue-query'
import { getDrillsWithStatistics, getDrillsPredefined } from '@/api/drill/getList'
import { createRoomFromDrill } from '@/api/createRoomFromDrill'

import { useDrillManagement } from './composables/use-drill-management'

import UiButton from '@/components/ui/button/Button.vue'
import SettingsSvg from '@/assets/icons/settings.svg'
import CloseSvg from '@/assets/X.svg'
import PlusSvg from '@/assets/plus.svg'
import DotsLoader from '@/components/DotsLoader.vue'
import DrillItem from './list/DrillItem.vue'
import EmptyList from './list/EmptyList.vue'
import DrillDetails from './list/DrillDetails.vue'
import ConfirmDelete from './list/ConfirmDelete.vue'
import ConfirmReset from './list/ConfirmReset.vue'

import type { Drill, GameFlavor, DrillWithStatistic } from '@acepoker/contract'
import { DrillManageActionType } from '@/types'

// TODO: load correct list of drills depends on game
const props = defineProps<{
  path: string
  flavors: GameFlavor[]
  manageable?: boolean
  category: string
  // TODO: filter
  filters: Record<string, string | number | boolean>
  isFiltered: boolean
}>()

const emits = defineEmits<{
  (e: 'drill-selected', id: DrillWithStatistic | null): void
}>()

const router = useRouter()

const { player } = storeToRefs(useAuth())
const playerId = computed(() => player.value?.player_id)

const isEmpty = computed(() => !data.value?.items.length)

const { isPending, data, error } = useQuery({
  queryKey: [
    'custom-drills',
    playerId,
    toRef(props, 'category'),
    toRef(props, 'flavors'),
    toRef(props, 'filters')
  ],
  queryFn: async ({ queryKey }) => {
    if (props.category === 'custom') {
      return getDrillsWithStatistics({
        flavors: props.flavors,
        filters: props.filters
      })
    } else {
      return getDrillsPredefined({
        filters: props.filters
      })
    }
  },
  retry: 1
})

const {
  handleManageAction,

  deleteConfirmModal,
  resetConfirmModal
} = useDrillManagement()

const selectedDrillId = ref<Drill['id'] | null>(null)
const selectedDrill = computed(() =>
  data.value?.items.find((el) => el.Drill.id === selectedDrillId.value)
)

function onDrillClick(id: Drill['id']) {
  selectedDrillId.value = id
  emits('drill-selected', selectedDrill.value)
}

const { mutate: createRoom, isPending: isCreatingRoom } = useMutation({
  mutationFn: (id: Drill['id']) => {
    return createRoomFromDrill(id)
  },
  onSuccess(data) {
    router.push({ name: 'Room', params: { id: data.id } })
  }
})

function resetSelection() {
  selectedDrillId.value = null
  emits('drill-selected', null)
}

defineExpose({
  resetSelection,
  isEmpty
})
</script>

<template>
  <div class="flex flex-col gap-2 overflow-y-auto">
    <div v-if="isPending" class="flex h-full items-center justify-center py-8">
      <dots-loader size="large" />
    </div>
    <div v-else-if="error" class="flex h-full items-center justify-center">
      <span class="text-[22px] leading-[26px] font-semibold text-white">{{
        $t('label.OopsError')
      }}</span>
    </div>
    <div v-else-if="data?.items && !data?.items.length" class="flex flex-col gap-2 overflow-y-auto">
      <empty-list :not-found="isFiltered">
        <ui-button
          type="button"
          variant="primary-homepage"
          class="w-full p-2 md:mx-auto md:max-w-80"
          @click="router.push({ path: `/games/${path}/new` })"
        >
          <div class="mr-2 rounded-sm border border-white/12 bg-white/15">
            <PlusSvg class="w-6" />
          </div>
          <span class="grow pr-8">{{ $t('CustomDrills.button.CreateNewDrill') }}</span>
        </ui-button>
      </empty-list>
    </div>
    <div
      v-else-if="data?.items && data?.items.length"
      class="flex h-full flex-col gap-2 overflow-y-auto py-2"
    >
      <drill-item
        v-for="item in data.items"
        :key="item.Drill.id"
        :drill-data="item"
        :editing="manageable"
        @click="onDrillClick(item.Drill.id)"
        @onManageActionSelect="
          (type) =>
            handleManageAction(
              type,
              { id: item.Drill.id, path },
              type === DrillManageActionType.delete && resetSelection
            )
        "
        :selected="item.Drill.id === selectedDrillId"
      />
    </div>

    <ConfirmDelete ref="deleteConfirmModal" />
    <ConfirmReset ref="resetConfirmModal" />
  </div>
</template>

<style lang="scss" scoped>
.drills-container {
  display: grid;
  grid-template-areas:
    'tabs'
    'list';
  grid-template-rows: [row1-start] 3rem [row1-end row2-start] auto [row2-end];
  grid-template-columns: 1fr;
  row-gap: 10px;
  column-gap: 20px;

  &--with-details {
    grid-template-areas:
      'tabs details'
      'list details';
    grid-template-columns: 1fr 2fr;
  }

  &__tabs {
    grid-area: tabs;
  }
  &__list {
    grid-area: list;
  }
  &__details {
    grid-area: details;

    &--mobile {
      position: fixed;
      top: 0;
      left: 0;
      width: 100dvw;
      height: 100dvh;
    }
  }
}
</style>
